import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Popover } from 'react-tiny-popover';

import Icon from '../../../misc/Icon';
import DecoratorIconOptions from '../../../../constants/DecoratorIconOptions';
import FieldModes from '../../../../constants/FieldModes';
import ThemeContext from '../../../../theme/ThemeContext';
import ThemeIcons from '../../../../theme/Icons';
import DecoratorConfigContext from '../../../DecoratorConfigContext';

const StyledPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid ${(props) => props.$color};
  background: none;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.$color};
  max-width: 100px;
  min-width: 0;
  flex-shrink: 0;
  line-height: 1.4;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: ${(props) => (props.$clickable ? `${props.$color}1a` : 'transparent')};
  }

  &:focus {
    outline: none;
  }
`;

const StyledPillLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const StyledTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  background: none;
  border: none;
  padding: 3px 4px;
  border-radius: 3px;
  opacity: 0.55;
  transition: opacity 0.15s ease-in-out, background-color 0.15s ease-in-out;

  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.06);
  }

  &:focus {
    outline: none;
  }
`;

const StyledMenu = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.$bg};
  border-radius: ${(props) => props.$radius}px;
  border: 1px solid ${(props) => props.$border};
  box-shadow: 0px 4px 20px rgba(20, 20, 23, 0.18);
  min-width: 200px;
  max-width: 280px;
  overflow: hidden;
  font-family: ${(props) => props.$fontFamily};
`;

const StyledDecoratorRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: background-color 0.1s ease-in-out;

  &:hover {
    background-color: ${(props) => (props.$clickable ? props.$hover : 'transparent')};
  }

  & + & {
    border-top: 1px solid ${(props) => props.$border};
  }
`;

const StyledIconWrapper = styled.div`
  flex-shrink: 0;
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 13px;
  }
`;

const StyledRowLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.$color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const StyledRowDecoratorScroll = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  max-width: 100%;
  min-width: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const getIconDefinition = (iconValue) => {
  if (!iconValue) return null;
  const match = DecoratorIconOptions.find((opt) => opt.value === iconValue);
  return match ? match.icon : ThemeIcons.decorator.image;
};

const InputGroupRowDecorators = ({ decorators, fieldMode, rowId, onDecoratorClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useContext(ThemeContext);
  const { visibleLimitInRows } = useContext(DecoratorConfigContext);

  if (fieldMode === FieldModes.pdf) return null;

  if (!decorators || !Array.isArray(decorators) || decorators.length === 0) return null;

  const validDecorators = decorators.filter((d) => d.icon || d.label);
  if (validDecorators.length === 0) return null;

  const isClickable = fieldMode === FieldModes.fill || fieldMode === FieldModes.readonly;

  const handleClick = (decorator) => {
    if (!isClickable) return;
    setIsOpen(false);
    if (onDecoratorClick) onDecoratorClick(decorator, rowId);
  };

  if (validDecorators.length <= visibleLimitInRows) {
    return (
      <StyledRowDecoratorScroll>
        {validDecorators.map((decorator, index) => {
          const iconDef = getIconDefinition(decorator.icon);
          const color = decorator.color || theme.primaryColor;
          return (
            <StyledPill
              key={decorator._id || index}
              type="button"
              $color={color}
              $clickable={isClickable}
              onClick={(e) => {
                e.stopPropagation();
                handleClick(decorator);
              }}
            >
              {iconDef ? <Icon icon={iconDef} color={color} /> : null}
              {decorator.label ? <StyledPillLabel>{decorator.label}</StyledPillLabel> : null}
            </StyledPill>
          );
        })}
      </StyledRowDecoratorScroll>
    );
  }

  const menuContent = (
    <StyledMenu
      $bg={theme.backgroundColor}
      $radius={theme.borderRadius}
      $border={theme.borderColor}
      $fontFamily={theme.fontFamily}
    >
      {validDecorators.map((decorator, index) => {
        const iconDef = getIconDefinition(decorator.icon);
        const color = decorator.color || theme.primaryColor;

        return (
          <StyledDecoratorRow
            key={decorator._id || index}
            $clickable={isClickable}
            $hover={theme.secondaryColor}
            $border={theme.borderColor}
            onClick={() => handleClick(decorator)}
          >
            {iconDef ? (
              <StyledIconWrapper>
                <Icon icon={iconDef} color={color} />
              </StyledIconWrapper>
            ) : null}
            {decorator.label ? (
              <StyledRowLabel $color={color}>{decorator.label}</StyledRowLabel>
            ) : null}
          </StyledDecoratorRow>
        );
      })}
    </StyledMenu>
  );

  return (
    <Popover
      isOpen={isOpen}
      positions={['bottom', 'top', 'right', 'left']}
      reposition={true}
      containerStyle={{ zIndex: '2147483647' }}
      onClickOutside={() => setIsOpen(false)}
      content={menuContent}
    >
      <StyledTrigger
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <Icon icon={ThemeIcons.misc.decorators} />
      </StyledTrigger>
    </Popover>
  );
};

InputGroupRowDecorators.propTypes = {
  decorators: PropTypes.array,
  fieldMode: PropTypes.string,
  rowId: PropTypes.string,
  onDecoratorClick: PropTypes.func,
};

export default InputGroupRowDecorators;
